import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  FileText,
  MessageSquare,
  Search,
  Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  waLogApi,
  type WaConversation,
  type WaLoggingSettings,
  type WaMessage,
} from '@/api/waLog';
import { Badge, EmptyState, Loading, Modal } from '@/components/ui';
import { formatDateTime, formatRelative } from '@/utils/date';
import { cn } from '@/utils/helpers';

const PAGE_SIZE = 25;
const THREAD_PAGE_SIZE = 40;
const SEARCH_PAGE_SIZE = 30;

const WARNING_BOT =
  'כשהתיעוד כבוי, גם הבוט האוטומטי והמשימות האוטומטיות לא פועלים לארגון זה.';
const WARNING_PROPAGATION = 'שינוי נכנס לתוקף תוך כ-30 שניות.';

function Toggle({
  checked,
  onChange,
  disabled,
  label,
  id,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
  id: string;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex items-center justify-between gap-3 select-none',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      <span className="text-sm text-ink-primary">{label}</span>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          'relative h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gold/40',
          checked ? 'bg-gold' : 'bg-navy-border',
          disabled && 'pointer-events-none',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0.5',
          )}
        />
      </button>
    </label>
  );
}

function conversationTitle(c: WaConversation): string {
  if (c.is_group) return c.group_subject || c.display_name || 'קבוצה';
  return c.display_name || c.phone || c.jid || 'שיחה';
}

const MEDIA_TYPE_HE: Record<string, string> = {
  image: 'תמונה',
  photo: 'תמונה',
  imagemessage: 'תמונה',
  document: 'מסמך',
  documentmessage: 'מסמך',
  file: 'מסמך',
  audio: 'אודיו',
  ptt: 'אודיו',
  voicenote: 'אודיו',
  audiomessage: 'אודיו',
  video: 'וידאו',
  videomessage: 'וידאו',
  sticker: 'מדבקה',
  stickermessage: 'מדבקה',
};

function mediaLabel(m: WaMessage): string | null {
  const raw = (m.message_type ?? '').trim();
  if (!raw || raw.toLowerCase() === 'text') return null;
  const he = MEDIA_TYPE_HE[raw.toLowerCase()] ?? raw;
  if (m.media_filename) return `${he} · ${m.media_filename}`;
  return he;
}

/** Outbound origin label; null → render nothing (null legacy rows, inbound sources). */
function sourceBadge(source: string | null | undefined): string | null {
  if (!source) return null;
  switch (source) {
    case 'hub':
      return 'נשלח מהמערכת';
    case 'bot':
      return 'בוט';
    case 'automation':
      return 'אוטומציה';
    case 'otomator':
      return 'אוטומטור';
    case 'legacy':
      return 'מערכת ישנה';
    case 'inbound':
    case 'meta-inbound':
      return null;
    default:
      return null;
  }
}

function MessageRow({
  msg,
  onOpenThread,
}: {
  msg: WaMessage;
  onOpenThread?: (conversationId: string) => void;
}) {
  // DB stores direction as 'in' | 'out' (not inbound/outbound).
  const isOut = msg.direction === 'out';
  // RTL + WhatsApp HE: own messages on the LEFT. flex dir=rtl: start=right, end=left
  // so outbound uses justify-end → left edge.
  const media = mediaLabel(msg);
  const origin = isOut ? sourceBadge(msg.source) : null;
  return (
    <div className={cn('flex', isOut ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm',
          isOut
            ? 'bg-navy-card border border-navy-border text-slate-200 rounded-bl-sm'
            : 'bg-teal/20 border border-teal/30 text-slate-100 rounded-br-sm',
        )}
      >
        {msg.body ? (
          <p className="leading-relaxed whitespace-pre-wrap break-words">{msg.body}</p>
        ) : media ? (
          <p className="text-slate-400 italic">{media}</p>
        ) : (
          <p className="text-slate-500 italic">(ללא תוכן)</p>
        )}
        {media && msg.body ? (
          <p className="mt-1 text-[11px] text-slate-500 flex items-center gap-1">
            <FileText className="h-3 w-3 shrink-0" />
            {media}
          </p>
        ) : null}
        <div
          className={cn(
            'mt-1 flex flex-wrap items-center gap-2 text-[10px]',
            isOut ? 'text-slate-500' : 'text-teal/70',
          )}
        >
          <span className="num">
            {msg.timestamp ? formatDateTime(msg.timestamp) : '—'}
          </span>
          {origin ? (
            <span className="text-slate-600">{origin}</span>
          ) : null}
          {msg.status ? <span>· {msg.status}</span> : null}
          {onOpenThread ? (
            <button
              type="button"
              onClick={() => onOpenThread(msg.conversation_id)}
              className="underline underline-offset-2 hover:text-gold"
            >
              פתח שיחה
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  return (
    <div className="flex items-center justify-between gap-3 pt-2 text-xs text-ink-secondary">
      <span className="num">
        עמוד {page} מתוך {pages} ({total})
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="px-2.5 py-1 rounded-lg border border-navy-border hover:bg-navy-hover disabled:opacity-40"
        >
          הקודם
        </button>
        <button
          type="button"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          className="px-2.5 py-1 rounded-lg border border-navy-border hover:bg-navy-hover disabled:opacity-40"
        >
          הבא
        </button>
      </div>
    </div>
  );
}

export default function OrgWaLogTab({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const [confirmOffOpen, setConfirmOffOpen] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [listPage, setListPage] = useState(1);
  const [threadPage, setThreadPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSubmitted, setSearchSubmitted] = useState('');
  const [searchPage, setSearchPage] = useState(1);

  const settingsQuery = useQuery({
    queryKey: ['wa-log-settings', orgId],
    queryFn: () => waLogApi.getSettings(orgId),
  });

  const conversationsQuery = useQuery({
    queryKey: ['wa-log-conversations', orgId, listPage],
    queryFn: () => waLogApi.listConversations(orgId, listPage, PAGE_SIZE),
  });

  const threadQuery = useQuery({
    queryKey: ['wa-log-thread', orgId, selectedConversationId, threadPage],
    queryFn: () =>
      waLogApi.getThread(orgId, selectedConversationId!, threadPage, THREAD_PAGE_SIZE),
    enabled: !!selectedConversationId,
  });

  const searchQueryResult = useQuery({
    queryKey: ['wa-log-search', orgId, searchSubmitted, searchPage],
    queryFn: () => waLogApi.search(orgId, searchSubmitted, searchPage, SEARCH_PAGE_SIZE),
    enabled: searchSubmitted.length > 0,
  });

  const settings = settingsQuery.data?.settings;
  const counts = settingsQuery.data?.counts;

  const setSettingsMutation = useMutation({
    mutationFn: (patch: {
      enabled?: boolean;
      logMedia?: boolean;
      logGroups?: boolean;
    }) => waLogApi.setSettings(orgId, patch),
    onSuccess: () => {
      toast.success('ההגדרות נשמרו');
      void queryClient.invalidateQueries({ queryKey: ['wa-log-settings', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['wa-log-conversations', orgId] });
      setConfirmOffOpen(false);
    },
    onError: (err: Error) => toast.error(err.message || 'שגיאה בשמירה'),
  });

  const neverEnabled = useMemo(() => {
    if (!settings) return false;
    return !settings.enabled && !settings.enabled_at && !settings.disabled_at;
  }, [settings]);

  const enabledButEmpty =
    !!settings?.enabled &&
    (counts?.messages ?? 0) === 0 &&
    (counts?.conversations ?? 0) === 0;

  const onMainToggle = (next: boolean) => {
    if (!next && settings?.enabled) {
      setConfirmOffOpen(true);
      return;
    }
    setSettingsMutation.mutate({ enabled: next });
  };

  const selectedConversation = conversationsQuery.data?.conversations.find(
    (c) => c.id === selectedConversationId,
  );

  return (
    <div className="space-y-6" dir="rtl">
      {/* ── 1. Switch panel ─────────────────────────────────────────────── */}
      <section className="card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-gold" />
          <h3 className="text-sm font-semibold text-ink-primary">תיעוד שיחות WhatsApp</h3>
        </div>

        {settingsQuery.isLoading ? (
          <Loading text="טוען הגדרות..." />
        ) : settingsQuery.isError ? (
          <p className="text-sm text-danger">
            {(settingsQuery.error as Error).message || 'שגיאה בטעינת ההגדרות'}
          </p>
        ) : settings ? (
          <>
            <Toggle
              id="wa-log-enabled"
              label="שמירת תיעוד שיחות"
              checked={!!settings.enabled}
              disabled={setSettingsMutation.isPending}
              onChange={onMainToggle}
            />

            <div className="ps-1 space-y-2 border-s-2 border-navy-border ms-1">
              <div className="ps-3">
                <Toggle
                  id="wa-log-media"
                  label="כולל מדיה"
                  checked={!!settings.log_media}
                  disabled={!settings.enabled || setSettingsMutation.isPending}
                  onChange={(v) => setSettingsMutation.mutate({ logMedia: v })}
                />
              </div>
              <div className="ps-3">
                <Toggle
                  id="wa-log-groups"
                  label="כולל קבוצות"
                  checked={!!settings.log_groups}
                  disabled={!settings.enabled || setSettingsMutation.isPending}
                  onChange={(v) => setSettingsMutation.mutate({ logGroups: v })}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-4 text-xs text-ink-secondary">
              <span>
                שיחות:{' '}
                <span className="num text-ink-primary font-semibold">
                  {counts?.conversations ?? 0}
                </span>
              </span>
              <span>
                הודעות:{' '}
                <span className="num text-ink-primary font-semibold">
                  {counts?.messages ?? 0}
                </span>
              </span>
              {settings.enabled_at ? (
                <span>הופעל ב-{formatDateTime(settings.enabled_at)}</span>
              ) : null}
              {settings.disabled_at && !settings.enabled ? (
                <span>כובה ב-{formatDateTime(settings.disabled_at)}</span>
              ) : null}
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-amber-200 text-xs leading-relaxed">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{WARNING_BOT}</span>
              </div>
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-amber-200 text-xs leading-relaxed">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{WARNING_PROPAGATION}</span>
              </div>
            </div>
          </>
        ) : null}
      </section>

      {/* ── 2+3. Conversation list + thread ─────────────────────────────── */}
      <section className="card overflow-hidden flex flex-col min-h-0" style={{ maxHeight: '36rem' }}>
        <div className="px-5 py-3 border-b border-navy-border flex items-center gap-2 shrink-0">
          <Users className="h-4 w-4 text-gold" />
          <h3 className="text-sm font-semibold text-ink-primary">שיחות</h3>
          {selectedConversationId ? (
            <button
              type="button"
              onClick={() => {
                setSelectedConversationId(null);
                setThreadPage(1);
              }}
              className="ms-auto flex items-center gap-1 text-xs text-gold hover:underline"
            >
              <ArrowRight className="h-3.5 w-3.5" />
              חזרה לרשימה
            </button>
          ) : null}
        </div>

        {!selectedConversationId ? (
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            {conversationsQuery.isLoading ? (
              <Loading text="טוען שיחות..." />
            ) : conversationsQuery.isError ? (
              <p className="text-sm text-danger">
                {(conversationsQuery.error as Error).message || 'שגיאה בטעינת השיחות'}
              </p>
            ) : neverEnabled ? (
              <EmptyState
                icon={<MessageSquare className="h-10 w-10" />}
                title="התיעוד לא הופעל מעולם"
                description="הפעילו את «שמירת תיעוד שיחות» כדי להתחיל לשמור הודעות לארגון זה."
              />
            ) : enabledButEmpty || (conversationsQuery.data?.total ?? 0) === 0 ? (
              <EmptyState
                icon={<MessageSquare className="h-10 w-10" />}
                title="אין הודעות עדיין"
                description="התיעוד פעיל, אך עדיין לא נשמרו שיחות לארגון זה."
              />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-start">
                    <thead>
                      <tr className="text-xs text-ink-secondary border-b border-navy-border">
                        <th className="py-2 pe-3 font-medium text-start">שם / טלפון</th>
                        <th className="py-2 pe-3 font-medium text-start">תצוגה מקדימה</th>
                        <th className="py-2 pe-3 font-medium text-start">זמן</th>
                        <th className="py-2 font-medium text-start">הודעות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(conversationsQuery.data?.conversations ?? []).map((c) => (
                        <tr
                          key={c.id}
                          onClick={() => {
                            setSelectedConversationId(c.id);
                            setThreadPage(1);
                          }}
                          className="border-b border-navy-border/60 hover:bg-navy-hover/50 cursor-pointer"
                        >
                          <td className="py-2.5 pe-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-ink-primary font-medium">
                                {conversationTitle(c)}
                              </span>
                              {c.is_group ? <Badge variant="neutral">קבוצה</Badge> : null}
                            </div>
                            {c.phone && !c.is_group ? (
                              <div className="text-[11px] text-ink-muted num mt-0.5">{c.phone}</div>
                            ) : null}
                          </td>
                          <td className="py-2.5 pe-3 text-ink-secondary max-w-[14rem] truncate">
                            {c.last_message_preview || '—'}
                          </td>
                          <td className="py-2.5 pe-3 text-ink-secondary text-xs whitespace-nowrap">
                            {c.last_message_at ? formatRelative(c.last_message_at) : '—'}
                          </td>
                          <td className="py-2.5 num text-ink-secondary">{c.message_count ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={listPage}
                  pageSize={PAGE_SIZE}
                  total={conversationsQuery.data?.total ?? 0}
                  onPage={setListPage}
                />
              </>
            )}
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="px-4 py-2 border-b border-navy-border shrink-0 text-xs text-ink-secondary">
              {selectedConversation
                ? conversationTitle(selectedConversation)
                : 'שיחה'}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
              {threadQuery.isLoading ? (
                <Loading text="טוען הודעות..." />
              ) : threadQuery.isError ? (
                <p className="text-sm text-danger">
                  {(threadQuery.error as Error).message || 'שגיאה בטעינת ההודעות'}
                </p>
              ) : (threadQuery.data?.messages.length ?? 0) === 0 ? (
                <EmptyState
                  icon={<MessageSquare className="h-10 w-10" />}
                  title="אין הודעות בשיחה"
                  description="לא נשמרו הודעות עבור שיחה זו."
                />
              ) : (
                (threadQuery.data?.messages ?? []).map((m) => (
                  <MessageRow key={m.id} msg={m} />
                ))
              )}
            </div>
            <div className="px-4 pb-3 shrink-0">
              <Pagination
                page={threadPage}
                pageSize={THREAD_PAGE_SIZE}
                total={threadQuery.data?.total ?? 0}
                onPage={setThreadPage}
              />
            </div>
          </div>
        )}
      </section>

      {/* ── 4. Search ───────────────────────────────────────────────────── */}
      <section className="card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-gold" />
          <h3 className="text-sm font-semibold text-ink-primary">חיפוש בהודעות</h3>
        </div>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const q = searchQuery.trim();
            setSearchSubmitted(q);
            setSearchPage(1);
          }}
        >
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="חיפוש בתוכן הודעות..."
            dir="rtl"
            className="flex-1 h-9 px-3 rounded-lg bg-navy-dark border border-navy-border text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-gold/30"
          />
          <button
            type="submit"
            className="h-9 px-4 rounded-lg bg-gold text-navy-dark text-sm font-bold hover:bg-gold/90 disabled:opacity-40"
          >
            חפש
          </button>
        </form>

        {searchSubmitted ? (
          <div className="max-h-80 min-h-0 overflow-y-auto space-y-2">
            {searchQueryResult.isLoading ? (
              <Loading text="מחפש..." />
            ) : searchQueryResult.isError ? (
              <p className="text-sm text-danger">
                {(searchQueryResult.error as Error).message || 'שגיאה בחיפוש'}
              </p>
            ) : (searchQueryResult.data?.messages.length ?? 0) === 0 ? (
              <EmptyState
                icon={<Search className="h-10 w-10" />}
                title="לא נמצאו תוצאות"
                description={`לא נמצאו הודעות התואמות ל־«${searchSubmitted}».`}
              />
            ) : (
              (searchQueryResult.data?.messages ?? []).map((m) => (
                <MessageRow
                  key={m.id}
                  msg={m}
                  onOpenThread={(id) => {
                    setSelectedConversationId(id);
                    setThreadPage(1);
                  }}
                />
              ))
            )}
            <Pagination
              page={searchPage}
              pageSize={SEARCH_PAGE_SIZE}
              total={searchQueryResult.data?.total ?? 0}
              onPage={setSearchPage}
            />
          </div>
        ) : null}
      </section>

      <Modal
        isOpen={confirmOffOpen}
        onClose={() => setConfirmOffOpen(false)}
        title="כיבוי תיעוד שיחות"
        footer={(
          <>
            <button
              type="button"
              onClick={() => setConfirmOffOpen(false)}
              disabled={setSettingsMutation.isPending}
              className="px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary"
            >
              ביטול
            </button>
            <button
              type="button"
              onClick={() => setSettingsMutation.mutate({ enabled: false })}
              disabled={setSettingsMutation.isPending}
              className="px-4 py-2 text-sm font-bold rounded-lg bg-danger text-white hover:bg-danger/90 disabled:opacity-40"
            >
              {setSettingsMutation.isPending ? 'מכבה...' : 'כבה תיעוד'}
            </button>
          </>
        )}
      >
        <div className="space-y-3 text-sm text-ink-secondary min-h-0">
          <p>
            ההיסטוריה שכבר נשמרה תישאר. רק הודעות חדשות יפסיקו להישמר, והבוט האוטומטי
            והמשימות האוטומטיות לא יפעלו עד להפעלה מחדש.
          </p>
          <p className="text-amber-200 text-xs">{WARNING_PROPAGATION}</p>
        </div>
      </Modal>
    </div>
  );
}

// silence unused type import if tree-shaken in some builds
export type { WaLoggingSettings };
